import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Check, FileText } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SectionHeading, Icon, CTABand } from "../components/blocks";
import { SERVICES, IMAGES } from "../lib/data";

export default function Prestations() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  return (
    <div>
      <Seo
        title="Nos prestations — Toiture, façade, isolation | Carnier Toiture (Oise)"
        description="Rénovation et entretien de couverture, démoussage, traitement de toiture, gouttières zinc/PVC, ravalement, peinture extérieure et isolation dans l'Oise."
      />

      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">Nos prestations</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-balance">Des solutions complètes pour votre habitat</h1>
            <p className="mt-5 max-w-2xl text-vegetal-100/80 text-lg">
              De la toiture aux façades, découvrez comment nous protégeons, rénovons et valorisons votre maison dans l'Oise et ses environs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Quick nav */}
      <section className="border-b border-neutral-200 bg-white/60 sticky top-[72px] z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-3 flex gap-2 overflow-x-auto">
          {SERVICES.map((s) => (
            <a key={s.slug} href={`#${s.slug}`} className="whitespace-nowrap px-3 py-1.5 rounded-full text-sm text-neutral-700 hover:bg-terracotta-50 hover:text-terracotta-600 transition-colors border border-neutral-200">
              {s.title}
            </a>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 space-y-20">
        {SERVICES.map((s, i) => (
          <section key={s.slug} id={s.slug} className="scroll-mt-40">
            <div className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <Reveal>
                <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
                  <img src={IMAGES[s.image]} alt={s.title} className="w-full h-[300px] md:h-[380px] object-cover" />
                </div>
              </Reveal>
              <Reveal delay={100}>
                <div>
                  <div className="grid place-items-center h-12 w-12 rounded-lg bg-terracotta-50 text-terracotta-600">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </div>
                  <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold text-neutral-900">{s.title}</h2>
                  <p className="mt-4 text-neutral-700 leading-relaxed">{s.short}</p>
                  <div className="mt-5 flex items-start gap-3 rounded-lg bg-vegetal-50 border border-vegetal-100 p-4">
                    <Check className="h-5 w-5 text-vegetal-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-vegetal-800 font-medium">{s.benefit}</p>
                  </div>
                  <Link to="/devis" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium">
                    <FileText className="h-4 w-4" /> Devis pour « {s.title} »
                  </Link>
                </div>
              </Reveal>
            </div>
          </section>
        ))}
      </div>

      <CTABand />
    </div>
  );
}
